import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useShopifyProducts } from "@/hooks/useShopify";
import ProductCard from "@/components/ProductCard";

/**
 * WishlistPage — /wishlist
 * Renders the list of products the user has hearted. Powered by
 * StoreContext.wishlist (persisted in localStorage) + live Shopify catalog.
 */
const WishlistPage = () => {
  const { wishlist } = useStore();
  const { products, loading } = useShopifyProducts({ first: 100 });

  const savedProducts = useMemo(() => {
    if (!products?.length || !wishlist?.length) return [];
    const set = new Set(wishlist);
    return products.filter((p) => set.has(p.id));
  }, [products, wishlist]);

  return (
    <section
      className="min-h-[70vh] py-14 lg:py-20"
      style={{ background: "#FDF6F0" }}
      data-testid="wishlist-page"
    >
      <div className="max-w-[1240px] mx-auto px-5 lg:px-10">
        {/* Header */}
        <div className="mb-10 lg:mb-14 text-center">
          <p
            className="text-[10.5px] tracking-[0.42em] uppercase font-semibold mb-3 flex items-center justify-center gap-3"
            style={{ color: "#C5985A" }}
          >
            <span className="h-px w-8 bg-gold/50" />
            Your saved edit
            <span className="h-px w-8 bg-gold/50" />
          </p>
          <h1
            className="font-serif text-plum leading-[1.05]"
            style={{ fontSize: "clamp(34px, 5vw, 60px)" }}
          >
            The <span className="italic text-blush">Wishlist</span>
          </h1>
          <p className="mt-3 text-plum/65 text-sm lg:text-base font-serif italic">
            {savedProducts.length > 0
              ? `${savedProducts.length} piece${savedProducts.length === 1 ? "" : "s"} you&apos;ve hearted — they&apos;re saved on this device.`
              : "Tap the heart on any piece you love. It&apos;ll live here, safe."}
          </p>
        </div>

        {/* Empty state */}
        {!loading && savedProducts.length === 0 && (
          <div
            className="max-w-[520px] mx-auto text-center py-14 rounded-[24px] border"
            style={{
              background: "#FBEAF0",
              borderColor: "rgba(197,152,90,0.32)",
            }}
            data-testid="wishlist-empty"
          >
            <div className="w-16 h-16 rounded-full bg-cream mx-auto flex items-center justify-center mb-5 ring-1 ring-gold/30">
              <Heart size={22} className="text-blush" />
            </div>
            <h2 className="font-serif text-plum text-[26px] mb-2">
              Nothing here yet
            </h2>
            <p className="text-plum/65 text-sm mb-6 px-6">
              Browse the shop and heart the pieces you would like to come back to.
            </p>
            <Link to="/shop" className="btn-primary" data-testid="wishlist-shop-cta">
              <ArrowLeft size={14} /> Shop the edit
            </Link>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/5] rounded-[18px] bg-plum/5 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Product grid */}
        {!loading && savedProducts.length > 0 && (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7"
            data-testid="wishlist-grid"
          >
            {savedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Sync note */}
        {savedProducts.length > 0 && (
          <p
            className="mt-10 text-center text-[10.5px] tracking-[0.32em] uppercase font-semibold text-plum/50"
            data-testid="wishlist-sync-note"
          >
            <span className="text-gold" aria-hidden>✦</span> Saved on this device
            · Sign-in coming soon to sync across devices
          </p>
        )}
      </div>
    </section>
  );
};

export default WishlistPage;
